import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft,
  VideoCamera, 
  FileText, 
  Brain,
  Lock,
  CheckCircle,
  Clock,
  Play,
  Spinner,
  Certificate
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function toDirectImageUrl(url) {
  if (!url) return url;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) return `https://drive.google.com/thumbnail?id=${driveMatch[1]}&sz=w1280`;
  const driveOpenMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (driveOpenMatch) return `https://drive.google.com/thumbnail?id=${driveOpenMatch[1]}&sz=w1280`;
  return url;
}

export function CourseDetail() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses/${courseId}`, {
        withCredentials: true
      });
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/courses/${courseId}` } } });
      return;
    }

    setEnrolling(true);
    try {
      await axios.post(`${API_URL}/api/enrollments`, {
        course_id: courseId
      }, { withCredentials: true });
      fetchCourse();
    } catch (error) {
      console.error('Error enrolling:', error);
    } finally {
      setEnrolling(false);
    }
  };

  const handleGenerateCertificate = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/certificates/generate/${courseId}`,
        {},
        { withCredentials: true }
      );
      navigate(`/certificates/${response.data.id}`);
    } catch (error) {
      console.error('Error generating certificate:', error);
    }
  };

  const getModuleIcon = (type) => {
    switch (type) {
      case 'video': return VideoCamera;
      case 'pdf': return FileText;
      case 'quiz': return Brain;
      default: return FileText;
    }
  };

  const isModuleLocked = (module, index) => {
    if (!course.enrollment) return true;
    if (user?.role === 'admin') return false;
    if (index === 0) return false;
    
    const prevModules = course.modules.slice(0, index);
    return prevModules.some(m => !course.enrollment.completed_modules.includes(m.id));
  };

  const isModuleCompleted = (moduleId) => {
    return course.enrollment?.completed_modules?.includes(moduleId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-12 text-center">
          <h1 className="text-2xl font-bold text-[#1A252F]">Curso não encontrado</h1>
          <Link to="/courses">
            <Button className="mt-4">Voltar aos Cursos</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link to="/courses" className="inline-flex items-center gap-2 text-[#5C6B7A] hover:text-[#2C3E50] mb-6">
          <ArrowLeft size={20} />
          Voltar aos Cursos
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-[#2C3E50]/10 overflow-hidden">
              <img 
                src={toDirectImageUrl(course.thumbnail_url) || 'https://images.unsplash.com/photo-1765438868883-43efce4fd145?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHNlc3Npb258ZW58MHx8fHwxNzc1MzUxNTk2fDA&ixlib=rb-4.1.0&q=85'}
                alt={course.title}
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                {course.is_internal && (
                  <span className="inline-flex items-center gap-1 bg-[#2C3E50] text-white px-3 py-1 rounded-full text-xs font-medium mb-4">
                    <Lock size={12} />
                    Curso Interno
                  </span>
                )}
                <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-4">
                  {course.title}
                </h1>
                <p className="text-[#5C6B7A] leading-relaxed">
                  {course.description}
                </p>

                <div className="flex items-center gap-6 mt-6 text-sm text-[#5C6B7A]">
                  <div className="flex items-center gap-2">
                    <Clock size={18} />
                    <span>{course.estimated_duration} min</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText size={18} />
                    <span>{course.modules?.length || 0} módulos</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modules */}
            <div className="mt-8">
              <h2 className="text-xl font-semibold text-[#1A252F] mb-4">Módulos do Curso</h2>
              <div className="space-y-3">
                {course.modules?.map((module, index) => {
                  const Icon = getModuleIcon(module.content_type);
                  const locked = isModuleLocked(module, index);
                  const completed = isModuleCompleted(module.id);

                  return (
                    <div 
                      key={module.id}
                      className={`bg-white rounded-lg border border-[#2C3E50]/10 p-4 ${
                        locked ? 'opacity-60' : 'hover:shadow-md transition-shadow'
                      }`}
                      data-testid={`module-${module.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          completed ? 'bg-[#27AE60]' : locked ? 'bg-[#BDC3C7]' : 'bg-[#2C3E50]'
                        }`}>
                          {completed ? (
                            <CheckCircle size={20} className="text-white" weight="fill" />
                          ) : locked ? (
                            <Lock size={20} className="text-white" />
                          ) : (
                            <Icon size={20} className="text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-[#1A252F]">{module.title}</h3>
                          <p className="text-sm text-[#5C6B7A]">
                            {module.content_type.charAt(0).toUpperCase() + module.content_type.slice(1)} • {module.estimated_duration} min
                          </p>
                        </div>
                        {!locked && course.enrollment && (
                          <Link to={`/learn/${courseId}/${module.id}`}>
                            <Button 
                              size="sm" 
                              className="bg-[#2C3E50] text-white hover:bg-[#1A252F]"
                              data-testid={`start-module-${module.id}`}
                            >
                              <Play size={16} className="mr-1" />
                              {completed ? 'Revisar' : 'Iniciar'}
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-[#2C3E50]/10 p-6 sticky top-24">
              {course.enrollment ? (
                <>
                  <h3 className="font-semibold text-[#1A252F] mb-4">Seu Progresso</h3>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#5C6B7A]">Conclusão</span>
                      <span className="font-medium text-[#27AE60]">
                        {Math.round(course.enrollment.progress_percent)}%
                      </span>
                    </div>
                    <Progress value={course.enrollment.progress_percent} className="h-2" />
                  </div>
                  <p className="text-sm text-[#5C6B7A] mb-6">
                    {course.enrollment.completed_modules?.length || 0} de {course.modules?.length || 0} módulos concluídos
                  </p>

                  {course.enrollment.completed && (
                    <Button 
                      onClick={handleGenerateCertificate}
                      className="w-full bg-[#27AE60] text-white hover:bg-[#219653] mb-4"
                      data-testid="get-certificate-btn"
                    >
                      <Certificate size={18} className="mr-2" />
                      Obter Certificado
                    </Button>
                  )}

                  {course.modules?.length > 0 && !course.enrollment.completed && (
                    <Link to={`/learn/${courseId}/${
                      course.modules.find(m => !course.enrollment.completed_modules?.includes(m.id))?.id || course.modules[0].id
                    }`}>
                      <Button 
                        className="w-full bg-[#2C3E50] text-white hover:bg-[#1A252F]"
                        data-testid="continue-learning-btn"
                      >
                        <Play size={18} className="mr-2" />
                        Continuar Aprendendo
                      </Button>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-[#1A252F] mb-4">Comece Agora</h3>
                  <p className="text-sm text-[#5C6B7A] mb-6">
                    Matricule-se neste curso para acompanhar seu progresso e ganhar um certificado ao concluir.
                  </p>
                  <Button 
                    onClick={handleEnroll}
                    disabled={enrolling}
                    className="w-full bg-[#27AE60] text-white hover:bg-[#219653]"
                    data-testid="enroll-btn"
                  >
                    {enrolling ? (
                      <Spinner size={18} className="animate-spin" />
                    ) : (
                      <>Matricular-se - Grátis</>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseDetail;
